import type { ReactElement } from "react";
import { cloneElement } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSubscription } from "@/hooks/queries/use-billing";

/**
 * True when the caller's writes will be refused by the API (ENG-200-3).
 *
 * Defaults to `false` while loading and on error. That direction is
 * deliberate: this decides whether to *disable a button*, not whether a write
 * is permitted — the server decides that and has already refused anything it
 * should. Defaulting to blocked would grey out the UI for every paying
 * customer on every cold load, which is far likelier than the case it would
 * guard against.
 */
export function useWriteBlocked(): boolean {
  const { data } = useSubscription();
  if (!data || data.organizationless) {
    return false;
  }
  return data.access !== "full";
}

/**
 * Disables the control it wraps when the organization cannot write, and
 * explains why on hover.
 *
 * Disabling with an explanation is preferred over hiding: a control that
 * vanishes leaves the user hunting for a feature they remember existing,
 * whereas a disabled one with a reason tells them exactly what changed and
 * what to do about it.
 *
 * Renders the child untouched when writes are allowed — no wrapper element, no
 * tooltip, so an active organization sees byte-identical markup to before.
 *
 * @example
 * <WriteGuard>
 *   <Button onClick={createEpic}>New Epic</Button>
 * </WriteGuard>
 */
export function WriteGuard({
  children,
  reason,
}: {
  children: ReactElement<{ disabled?: boolean }>;
  reason?: string;
}) {
  const blocked = useWriteBlocked();

  if (!blocked) {
    return children;
  }

  const message =
    reason ??
    "Your organization is read-only. Visit Billing to restore write access.";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The span keeps the tooltip reachable: a disabled button emits no
            pointer events, so hovering it directly would never open the
            tooltip and the explanation would be unreachable. */}
        <span className="inline-flex" data-testid="write-guard-blocked">
          {cloneElement(children, { disabled: true })}
        </span>
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
}
